import { CheckCircle, Zap, Shield, Star, Clock, Users, Target, TrendingUp, HelpCircle, Lightbulb, ChevronDown, ShoppingCart, Award, Heart } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import HowItWorksStep from './HowItWorksStep';
import { useTranslations } from '@/hooks/useTranslations';
import { useStorefront } from '@/hooks/useStorefront';
import { sanitizeRichForStorefront } from '@/lib/productDescriptionAT';

const ICON_MAP: Record<string, React.ElementType> = {
  zap: Zap, shield: Shield, star: Star, clock: Clock, users: Users,
  target: Target, trending: TrendingUp, check: CheckCircle, lightbulb: Lightbulb,
  cart: ShoppingCart, award: Award, heart: Heart,
};

function getMetricLabel(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('follower')) return 'Follower';
  if (t.includes('like')) return 'Likes';
  if (t.includes('view') || t.includes('aufrufe')) return 'Views';
  if (t.includes('kommentar') || t.includes('comment')) return 'Kommentare';
  if (t.includes('share') || t.includes('teilen')) return 'Shares';
  if (t.includes('abonnent') || t.includes('subscriber')) return 'Abonnenten';
  return 'Interaktionen';
}

interface RichDescription {
  intro: string;
  benefits: { icon: string; title: string; text: string }[];
  howItWorks: { step: number; title: string; text: string }[];
  forWhom: { icon: string; title: string; text: string }[];
  whyBigFollow: { icon: string; title: string; text: string }[];
  tips: { title: string; text: string }[];
  faq: { question: string; answer: string }[];
}

interface Props {
  data: RichDescription;
  productTitle: string;
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen(!open)}
      className="w-full rounded-xl border bg-card p-6 text-left transition-all duration-200 hover:shadow-md"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="font-semibold">{question}</span>
        <ChevronDown className={cn("h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200", open && "rotate-180")} />
      </div>
      {open && (
        <p className="mt-4 border-t pt-4 text-muted-foreground leading-relaxed">{answer}</p>
      )}
    </button>
  );
}

export default function ProductDescription({ data, productTitle }: Props) {
  const { t } = useTranslations();
  const { storefront } = useStorefront();
  const rich = sanitizeRichForStorefront(data, storefront) as RichDescription;
  return (
    <div className="mt-16 space-y-16">
      {/* Intro */}
      <section>
        <h2 className="mb-5 text-2xl font-bold">{productTitle} – {t('rich.intro_suffix', 'Dein Weg zu mehr Reichweite')}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed text-pretty">{rich.intro}</p>
      </section>

      {/* Benefits */}
      {rich.benefits?.length > 0 && (
        <section>
          <h2 className="mb-8 text-2xl font-bold">{t('rich.benefits_title', 'Deine Vorteile auf einen Blick')}</h2>
          <div className="grid gap-5 sm:grid-cols-2">
            {rich.benefits.map((b, i) => {
              const Icon = ICON_MAP[b.icon] || CheckCircle;
              return (
                <div key={i} className="flex gap-5 rounded-xl border bg-card p-6 transition-all duration-200 hover:shadow-md">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{b.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{b.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* How it works */}
      {rich.howItWorks?.length > 0 && (
        <section>
          <h2 className="mb-8 text-2xl font-bold">{t('rich.how_it_works_title', 'So funktioniert\'s – In 3 einfachen Schritten')}</h2>
          <div className="grid gap-5 sm:grid-cols-3">
            {rich.howItWorks.map((step) => {
              const metricLabel = getMetricLabel(productTitle);
              return (
                <div key={step.step} className="flex flex-col items-center rounded-xl border bg-card p-6 text-center transition-all duration-200 hover:shadow-md">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-lg">
                    {step.step}
                  </div>
                  <h3 className="font-semibold">{step.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.text}</p>
                  <div className="mt-5 w-full border-t pt-5">
                    <HowItWorksStep step={step.step} metricLabel={metricLabel} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* For whom */}
      {rich.forWhom?.length > 0 && (
        <section>
          <h2 className="mb-8 text-2xl font-bold">{t('rich.for_whom_title', 'Für wen lohnt es sich?')}</h2>
          <div className="grid gap-5 sm:grid-cols-2">
            {rich.forWhom.map((item, i) => {
              const Icon = ICON_MAP[item.icon] || Users;
              return (
                <div key={i} className="rounded-xl border bg-card p-6 text-center transition-all duration-200 hover:shadow-md">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Why BigFollow */}
      {rich.whyBigFollow?.length > 0 && (
        <section className="rounded-2xl border bg-accent/30 p-8 sm:p-10">
          <h2 className="mb-8 text-2xl font-bold">{t('rich.why_bigfollow_title', 'Warum BigFollow?')}</h2>
          <div className="grid gap-6 sm:grid-cols-2">
            {rich.whyBigFollow.map((item, i) => {
              const Icon = ICON_MAP[item.icon] || Shield;
              return (
                <div key={i} className="flex gap-4 transition-all duration-200">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Tips */}
      {rich.tips?.length > 0 && (
        <section>
          <h2 className="mb-8 text-2xl font-bold flex items-center gap-2">
            <Lightbulb className="h-6 w-6 text-warning" />
            {t('rich.tips_title', 'Tipps für maximale Ergebnisse')}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {rich.tips.map((tip, i) => (
              <div key={i} className="flex gap-4 rounded-xl border bg-card p-6 transition-all duration-200 hover:shadow-md">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-warning/10 text-sm font-bold text-warning">
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-semibold">{tip.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{tip.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {rich.faq?.length > 0 && (
        <section>
          <h2 className="mb-8 text-2xl font-bold">{t('rich.faq_title', 'Häufig gestellte Fragen')}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {rich.faq.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
